import { useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { usePortalProfile } from '../api';
import { PageError, PageSkeleton } from '../frame/PageState';
import frame from '../frame/Portal.module.css';
import { usePortal } from '../frame/PortalLayout';
import type { PortalPendingChange, PortalProfileRow } from '../types';
import { PasswordDialog } from './PasswordDialog';
import { ProfileEdit } from './ProfileEdit';
import styles from './Profile.module.css';

/** The view rows a pending change belongs to (Portal 20 labels). */
const ROW_OF_FIELD: Record<PortalPendingChange['field'], string | null> = {
  phone: 'Telefon',
  address: null,
  net_income: 'Nettoeinkommen',
  special_payments: 'Sonderzahlungen',
  equity: 'Eigenkapital',
};

function Rows({
  rows,
  pending,
  reviewer,
  extra,
}: {
  rows: PortalProfileRow[];
  pending: PortalPendingChange[];
  reviewer: string;
  extra: PortalPendingChange[];
}) {
  return (
    <dl className={styles.rows}>
      {rows.map((row) => {
        const change = pending.find((item) => ROW_OF_FIELD[item.field] === row.label);
        return (
          <div key={row.label} className={styles.row}>
            <dt className={styles.rowLabel}>{row.label}</dt>
            <dd className={styles.rowValue}>
              <span className={clsx(row.highlighted && styles.highlighted)}>{row.value}</span>
              {change ? (
                <span className={styles.pendingNote}>
                  Wird von {reviewer} geprüft: {change.newValue ?? '—'}
                </span>
              ) : null}
            </dd>
          </div>
        );
      })}
      {extra.map((change) => (
        <div key={change.id} className={styles.row}>
          <dt className={styles.rowLabel}>{change.fieldLabel}</dt>
          <dd className={styles.rowValue}>
            <span>{change.oldValue ?? '—'}</span>
            <span className={styles.pendingNote}>
              Wird von {reviewer} geprüft: {change.newValue ?? '—'}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * `/portal/:orgSlug/profile` — Mandantenportal 20 (the self-disclosure's person and finance rows, „Zugang und
 * Sicherheit") and 21 (edit mode, `ProfileEdit`). Pending change requests show „Wird von <Berater> geprüft" (own).
 */
export function ProfilePage() {
  const { me } = usePortal();
  const profile = usePortalProfile();
  const [editing, setEditing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  if (profile.isPending) return <PageSkeleton blocks={[300, 80]} />;
  if (profile.isError) {
    return (
      <div className={frame.content}>
        <PageError
          what="Ihr Profil"
          error={profile.error}
          onRetry={() => void profile.refetch()}
          retrying={profile.isFetching}
        />
      </div>
    );
  }

  const data = profile.data;
  if (editing) return <ProfileEdit profile={data} onDone={() => setEditing(false)} />;

  const reviewer = me.advisor?.name ?? 'Ihrem Berater';
  const personExtra = data.pending.filter((change) => ROW_OF_FIELD[change.field] === null);
  return (
    <div className={clsx(frame.content, styles.page)}>
      <div className={frame.pageHead}>
        <div>
          <h1 className={frame.pageTitle}>Mein Profil</h1>
          <div className={frame.pageText}>{data.intro}</div>
        </div>
        <button type="button" className={clsx('pressable', frame.secondary)} onClick={() => setEditing(true)}>
          Angaben bearbeiten
        </button>
      </div>
      <div className={styles.grid}>
        <section className={clsx(frame.card, styles.card)} aria-labelledby="portal-profile-person">
          <h2 className={frame.eyebrow} id="portal-profile-person">
            Person
          </h2>
          <Rows rows={data.person} pending={data.pending} reviewer={reviewer} extra={personExtra} />
        </section>
        <section className={clsx(frame.card, styles.card)} aria-labelledby="portal-profile-finances">
          <h2 className={frame.eyebrow} id="portal-profile-finances">
            Finanzen
          </h2>
          <Rows rows={data.finances} pending={data.pending} reviewer={reviewer} extra={[]} />
        </section>
      </div>
      <section className={clsx(frame.card, styles.security)} aria-labelledby="portal-profile-security">
        <div className={styles.securityText}>
          <h2 className={styles.securityTitle} id="portal-profile-security">
            Zugang und Sicherheit
          </h2>
          <div className={styles.securityLine}>{data.security.text}</div>
        </div>
        <button
          type="button"
          className={clsx('pressable', frame.secondary, styles.smallButton)}
          onClick={() => setPasswordOpen(true)}
        >
          Passwort ändern
        </button>
      </section>
      <PasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </div>
  );
}
